import {
  FilteredBeneficiaryDetail,
  FilteredBlockchainBeneficiaryForPayment,
  FilteredFiatBeneficiary,
  FilteredResolvedBlockchainBeneficiary,
  LegalEntityType,
} from "@generated/types";

type FiatBeneficiaryForDisplay =
  | FilteredBeneficiaryDetail
  | FilteredFiatBeneficiary;

const getFiatBeneficiaryDisplayName = (
  beneficiary: FiatBeneficiaryForDisplay
): string => {
  const name = "name" in beneficiary ? beneficiary.name : undefined;
  return (
    beneficiary.nickname || name || beneficiary.bankAccount?.accountName || ""
  );
};

export const getBlockchainBeneficiaryName = (
  beneficiary:
    | FilteredResolvedBlockchainBeneficiary
    | FilteredBlockchainBeneficiaryForPayment
) => {
  if (!beneficiary?.legalEntity) return "";

  const { companyName, firstName, lastName, type } = beneficiary.legalEntity;

  if (type === LegalEntityType.Corporation && companyName) {
    return companyName;
  }

  if (type === LegalEntityType.Person && (firstName || lastName)) {
    return `${firstName || ""} ${lastName || ""}`.trim();
  }

  // Fallback to any available name
  return companyName || `${firstName || ""} ${lastName || ""}`.trim() || "";
};

export const getLegalEntityName = (
  beneficiary: FiatBeneficiaryForDisplay | string | undefined
) => {
  // Ledger entries fall back to the raw beneficiary UUID string when BFF
  // resolution fails — treat any non-object input as "no display name".
  if (!beneficiary || typeof beneficiary !== "object") return "";

  return getFiatBeneficiaryDisplayName(beneficiary);
};

export const getBeneficiaryDisplayName = (
  beneficiary: AnyBeneficiary | string | undefined
): string => {
  if (!beneficiary || typeof beneficiary === "string") return "";

  // Fiat beneficiaries
  if (beneficiary.resourceType === "beneficiaries") {
    return getLegalEntityName(beneficiary);
  }

  // Blockchain beneficiaries
  if (beneficiary.resourceType === "blockchain-beneficiaries") {
    return (
      beneficiary.nickname ||
      getBlockchainBeneficiaryName(
        beneficiary as
          | FilteredResolvedBlockchainBeneficiary
          | FilteredBlockchainBeneficiaryForPayment
      ) ||
      ""
    );
  }

  // Fallback when resourceType is not set (e.g. FilteredFiatBeneficiary from BFF).
  // Try fiat fields first, then blockchain fields.
  return (
    getLegalEntityName(beneficiary) ||
    getBlockchainBeneficiaryName(
      beneficiary as
        | FilteredResolvedBlockchainBeneficiary
        | FilteredBlockchainBeneficiaryForPayment
    ) ||
    ""
  );
};
